import { useIntl } from 'react-intl'
import { FILTER_TYPES, useFilters } from 'contrastBase/filters'
import { useDeepCompareMemo } from 'use-deep-compare'
import {
  FILTER_TYPE_MESSAGE_ID_MAP,
  getFilterLabel
} from '../../helpers/ui-interfaces'
import { LibraryFilterType } from '../../helpers/libraries-interfaces'
import { FilterDefinition } from '@contrast-security-inc/base-types'
import { AggregationFilter } from './types'

const initialFiltersDefinitions = (
  initFilters: LibraryFilterType[],
  excludeFilters: LibraryFilterType[]
) =>
  initFilters.reduce<FilterDefinition[]>((acc, filterKey) => {
    if ((excludeFilters || []).includes(filterKey)) {
      return acc
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    acc.push({
      filterMsgId: FILTER_TYPE_MESSAGE_ID_MAP[filterKey],
      filterKey,
      useFilterList: () => {
        return {
          isLoading: true,
          isError: false,
          data: { data: [] as any }
        }
      },
      filterType: FILTER_TYPES.SELECTION
    } as FilterDefinition)

    return acc
  }, [])

export const useFilterDefinitions = ({
  initialFilters,
  libraryFilters,
  excludeFilters,
  results
}: {
  initialFilters: LibraryFilterType[]
  libraryFilters: AggregationFilter[]
  excludeFilters: LibraryFilterType[]
  results: any
}) => {
  const { formatMessage } = useIntl()
  const filterDefs = useDeepCompareMemo(() => {
    const initFiltersResult = initialFiltersDefinitions(
      initialFilters,
      excludeFilters
    )

    const result = initFiltersResult.reduce<FilterDefinition[]>(
      (acc, initFilter) => {
        const libFilter = libraryFilters.find(
          (f) => f.key === initFilter.filterKey
        )

        // If there's no filter found and everything finished loading, then it's an error
        const isError = !libFilter && results.every((r) => !r.isLoading)

        if (isError) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          acc.push({
            ...initFilter,
            useFilterList: () => {
              return {
                isLoading: false,
                isError: true,
                data: { data: [] as any }
              }
            }
          } as FilterDefinition)
          return acc
        }

        if (libFilter) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const updatedFilter = {
            ...initFilter,
            filterKey: libFilter?.key,
            filterMsgId: initFilter.filterMsgId,
            filterType: FILTER_TYPES.SELECTION,
            options: { search: true },
            useFilterList: () => {
              return {
                isLoading: false,
                isError,
                data: {
                  data: {
                    filters:
                      libFilter.children?.map((value) => ({
                        label: getFilterLabel(value, formatMessage),
                        count: value.count,
                        keycode: value.key
                      })) || []
                  }
                }
              }
            }
          } as FilterDefinition

          acc.push(updatedFilter)
        } else {
          acc.push(initFilter)
        }

        return acc
      },
      []
    )

    return result
  }, [excludeFilters, formatMessage, initialFilters, libraryFilters, results])

  const filterState = useFilters({ filterDefs })

  return {
    filterState
  }
}
